/**
 * Навигационные гварды.
 *
 * ⚠️ ГЛАВНОЕ, ЧТО НАДО ЗНАТЬ ПРО ЭТОТ ФАЙЛ: **гвард — это UX-аффорданс, а не
 * авторизация.** Авторитет — Go-бэкенд: его RBAC fail-closed и вернёт 403
 * независимо от того, что решил роутер. Здесь мы всего лишь экономим человеку
 * заведомо отказной запрос и рисуем `/forbidden` вместо мигания пустой таблицей.
 * Ни одна проверка ниже не должна восприниматься как контроль доступа.
 */
import type { Router } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

/** Хвост `document.title`. */
const APP_TITLE = 'Админ-панель'

/**
 * Валидация `?redirect=` перед тем, как ей воспользоваться.
 *
 * Принимаем **только** строку, начинающуюся с одиночного `/`. Всё остальное —
 * `null`, то есть «редиректа не было».
 *
 * Что именно отсекается и почему:
 *   • `https://evil.com`  — абсолютный URL со схемой;
 *   • `//evil.com`        — protocol-relative: браузер читает это как хост,
 *                           а не как путь. Классический open-redirect в две строки;
 *   • `/\evil.com`        — браузеры нормализуют `\` в `/` в позиции authority,
 *                           так что это тот же `//evil.com` в маскировке;
 *   • не-строки           — `to.query.redirect` вполне может приехать массивом
 *                           (`?redirect=a&redirect=b`) или `null`.
 *
 * Экспортируется наружу: `LoginView` (F3-E) обязан гонять `route.query.redirect`
 * через эту же функцию, а не через свою копию проверки.
 */
export function safeRedirect(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  if (raw.length === 0 || raw[0] !== '/') return null
  // Второй символ `/` или `\` — попытка увести на чужой хост.
  if (raw[1] === '/' || raw[1] === '\\') return null
  return raw
}

/**
 * Вешает `beforeEach`/`afterEach` на переданный роутер.
 *
 * Отдельной функцией, а не побочным эффектом импорта: так порядок «создали
 * роутер → повесили гварды» виден в `index.ts`, а не зависит от порядка импортов.
 */
export function registerGuards(router: Router): void {
  router.beforeEach(async (to) => {
    const auth = useAuthStore()

    /**
     * ★ КЛЮЧЕВАЯ СТРОКА ФАЙЛА.
     *
     * `bootstrap()` ждём **до любого решения о редиректе**. Access-токен живёт
     * только в памяти, поэтому на холодной загрузке (F5, открытие ссылки из
     * мессенджера, восстановление вкладок) `isAuthenticated` === false даже при
     * совершенно живой 30-дневной сессии. Редирект на `/login` в этот момент
     * обесценил бы «Запомнить меня» целиком.
     *
     * Ждать его на каждой навигации не дорого: в сторе он мемоизирован и после
     * первого вызова возвращает уже разрешённый промис.
     */
    try {
      await auth.bootstrap()
    } catch {
      /**
       * Восстановить сессию не удалось — сеть легла, сервер отдал 500, refresh
       * протух. Это **не** повод вешать навигацию: продолжаем как аноним, и
       * ниже человек уедет на `/login`. Молчаливое падение гварда оставило бы
       * белый экран без единого следа в UI, что гораздо хуже.
       *
       * (Пока `stores/auth.ts` — заглушка F3-E, сюда прилетает её `TODO`-исключение;
       *  после F3-E здесь остаются только настоящие сетевые отказы.)
       */
    }

    // ── 1. Публичные маршруты: /login, 404 ──────────────────────────────
    if (to.meta.public) {
      if (auth.isAuthenticated && to.name === 'login') {
        // Уже вошли, а пришли на форму входа — уводим туда, куда собирались.
        const back = safeRedirect(to.query.redirect)
        // `back` на сам логин дал бы бесконечный цикл редиректов.
        if (back && router.resolve(back).name !== 'login') return back
        return { name: 'users' }
      }
      return true
    }

    // ── 2. Приватные маршруты: нужен вход ───────────────────────────────
    if (!auth.isAuthenticated) {
      // `fullPath`, а не `path`: у списка пользователей всё состояние (страница,
      // поиск, фильтр) лежит в query, и терять его при перелогине незачем.
      return { name: 'login', query: { redirect: to.fullPath } }
    }

    // ── 3. Ролевой гейт ─────────────────────────────────────────────────
    const allowed = to.meta.roles
    // Пустой массив — это «никому», а не «всем»: см. `types/router.d.ts`.
    if (allowed && (auth.role === null || !allowed.includes(auth.role))) {
      return { name: 'forbidden' }
    }

    return true
  })

  /**
   * Заголовок вкладки. В `afterEach`, а не в `beforeEach`, — заголовок должен
   * отражать то, куда мы **пришли**, а не то, куда собирались: отменённая или
   * перенаправленная навигация иначе оставила бы чужой заголовок.
   */
  router.afterEach((to) => {
    const title = to.meta.title
    document.title = title ? `${title} — ${APP_TITLE}` : APP_TITLE
  })
}
